import { MOBILE_NAV_ROUTES } from "@/components/layout/app-layout";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { useRouter } from "@tanstack/react-router";
import { type RefObject, useEffect } from "react";

const SWIPE_THRESHOLD = 50;
const INTENT_THRESHOLD = 10;
const EDGE_RESISTANCE = 0.3;
const SLIDE_OUT_DURATION = 200; // ms

function hasScrollableAncestor(
  target: EventTarget | null,
  container: HTMLElement,
): boolean {
  let el = target as HTMLElement | null;
  while (el && el !== container) {
    if (el.scrollWidth > el.clientWidth) {
      const style = window.getComputedStyle(el);
      const overflowX = style.getPropertyValue("overflow-x");
      if (overflowX === "auto" || overflowX === "scroll") {
        return true;
      }
    }
    el = el.parentElement;
  }
  return false;
}

export function useSwipeNavigation(ref: RefObject<HTMLElement | null>) {
  const isMobile = useIsMobile();
  const router = useRouter();

  useEffect(() => {
    const el = ref.current;
    if (!isMobile || !el) return;

    let startX = 0;
    let startY = 0;
    let deltaX = 0;
    let intentDecided = false;
    let isHorizontal = false;
    let isScrollable = false;

    function getCurrentIndex(): number {
      const path = router.state.location.pathname;
      const idx = MOBILE_NAV_ROUTES.indexOf(
        path as (typeof MOBILE_NAV_ROUTES)[number],
      );
      return idx === -1 ? -1 : idx;
    }

    function handleTouchStart(e: TouchEvent) {
      const touch = e.touches[0];
      if (!touch) return;
      startX = touch.clientX;
      startY = touch.clientY;
      deltaX = 0;
      intentDecided = false;
      isHorizontal = false;
      isScrollable = hasScrollableAncestor(e.target, el!);

      el!.style.transition = "none";
    }

    function handleTouchMove(e: TouchEvent) {
      if (isScrollable) return;

      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      if (!intentDecided) {
        if (
          Math.abs(dx) > INTENT_THRESHOLD ||
          Math.abs(dy) > INTENT_THRESHOLD
        ) {
          intentDecided = true;
          isHorizontal = Math.abs(dx) > Math.abs(dy);
        }
        if (!isHorizontal) return;
      }

      if (!isHorizontal) return;

      e.preventDefault();

      const currentIndex = getCurrentIndex();
      if (currentIndex === -1) return;

      const atLeftEdge = currentIndex === 0 && dx > 0;
      const atRightEdge =
        currentIndex === MOBILE_NAV_ROUTES.length - 1 && dx < 0;

      deltaX = atLeftEdge || atRightEdge ? dx * EDGE_RESISTANCE : dx;

      el!.style.transform = `translateX(${deltaX}px)`;
    }

    function handleTouchEnd() {
      const currentIndex = getCurrentIndex();

      if (currentIndex === -1 || !isHorizontal) {
        el!.style.transition = "";
        el!.style.transform = "";
        return;
      }

      const absDelta = Math.abs(deltaX);
      const swipingLeft = deltaX < 0;
      const swipingRight = deltaX > 0;

      const canGoNext =
        swipingLeft && currentIndex < MOBILE_NAV_ROUTES.length - 1;
      const canGoPrev = swipingRight && currentIndex > 0;

      if (absDelta > SWIPE_THRESHOLD && (canGoNext || canGoPrev)) {
        const nextIndex = canGoNext ? currentIndex + 1 : currentIndex - 1;
        const nextRoute = MOBILE_NAV_ROUTES[nextIndex];
        if (!nextRoute) return;

        const direction = canGoNext ? -1 : 1;
        const slideTarget = direction * el!.offsetWidth;

        el!.style.transition = `transform ${SLIDE_OUT_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        el!.style.transform = `translateX(${slideTarget}px)`;
        el!.style.opacity = "0";

        setTimeout(() => {
          el!.style.transition = "none";
          el!.style.transform = "";
          el!.style.opacity = "";
          router.navigate({ to: nextRoute });
        }, SLIDE_OUT_DURATION);
      } else {
        el!.style.transition = `transform ${SLIDE_OUT_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        el!.style.transform = "translateX(0)";
      }

      deltaX = 0;
      intentDecided = false;
      isHorizontal = false;
      isScrollable = false;
    }

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd);

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isMobile, router, ref]);
}
